import { FormControl } from "@mui/material";

import { useAssetSourceUrl, type FileAssetSource } from "@skilitics/assets";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { Label } from "../Labels";
import {
  assetSourceToMediaAsset,
  mediaAssetToAssetSource,
  useFieldMediaAssetInputProps,
  useMediaAssetUploadFileDropzoneProps,
  type FieldMediaAssetInputProps,
  type UploadOptions,
} from "./assets";
import type { FileDropzoneProps } from "./file-dropzone";
import { FileDropzone, UploadedMediaRow } from "./file-dropzone";
import { useFieldLabelAndDescription } from "./helpers";
import type { FileMediaAssetValue, MediaAssetType, MediaAssetValue } from "./types";

export type FieldMediaAssetProps<T extends MediaAssetValue = MediaAssetValue> = Omit<
  MediaAssetInputProps<T>,
  keyof FieldMediaAssetInputProps
> & {
  type: MediaAssetType;
};

export const FieldMediaAsset = <T extends MediaAssetValue = MediaAssetValue>(inputProps: FieldMediaAssetProps<T>) => {
  const mediaAssetProps = useFieldMediaAssetInputProps();
  const [label] = useFieldLabelAndDescription(mediaAssetProps.name);

  return (
    <FormControl>
      <Label htmlFor={mediaAssetProps.id} style={{ marginBottom: label ? "5px" : undefined }}>
        {label}
      </Label>
      <MediaAssetInput {...mediaAssetProps} {...inputProps} />
    </FormControl>
  );
};

export type MediaAssetInputProps<T extends MediaAssetValue = MediaAssetValue> = Omit<
  FileDropzoneProps,
  | "typeLabel"
  | "value"
  | "mimeType"
  | "mediaTypes"
  | "onChange"
  | "onFileSelected"
  | "onFileDeselected"
  | "onFileSelectionCancelled"
> & {
  type: MediaAssetType;
  value: T | undefined;
  setValue: (asset: T | undefined) => void;
  uploadOptions: UploadOptions;
};

export const MediaAssetInput = ({ value, setValue, uploadOptions, type, ...inputProps }: MediaAssetInputProps) => {
  const fileDropzoneProps = useMediaAssetUploadFileDropzoneProps(setValue, uploadOptions);

  const source = mediaAssetToAssetSource(value);
  if (source && !("assetId" in source)) throw new Error(`MediaAssetInput expects file assets only`);

  return (
    <FileDropzone empty={!value} {...fileDropzoneProps} {...inputProps}>
      <MediaAssetValueRow source={source} type={type} />
    </FileDropzone>
  );
};

export type MediaAssetValueRowProps = {
  type: MediaAssetType;
  source: FileAssetSource | undefined;
};

export const MediaAssetValueRow = ({ type, source }: MediaAssetValueRowProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  const url = useAssetSourceUrl(source);

  return (
    <>
      {source && url && <UploadedMediaRow media={assetSourceToMediaAsset(source) as FileMediaAssetValue} type={type} />}
      {source && !url && <p>{formatMessage("loading")}</p>}
    </>
  );
};

const formatStrings = {
  loading: {
    en: "Loading",
  },
} satisfies FormatMessages;
